import * as THREE from 'three';
import gsap from 'gsap';

/**
 * Allows the user to grab and move a target group (like the entire game world)
 * with direct 1-to-1 hand movement, and ensures the group always faces the user.
 * Includes configurable movement sensitivity and synchronized handle rotation.
 */
class GameMover {
    // --- Configuration ---
    colors = {
        default: 0x0077ff, // Blue
        hover: 0x88ccff,   // Light Blue
        grab: 0x00ff77,    // Green
    };
    opacities = {
        default: 0.5,
        hover: 0.7,
        grab: 1,
    };

    // --- Properties ---
    scene;
    gameGroup;
    controllers;
    camera;
    raycaster;

    grabHandle;
    handleMaterial;

    isGrabbing = false;
    isHovering = false;
    activeController = null;

    initialControllerPosition = new THREE.Vector3();
    initialGameGroupPosition = new THREE.Vector3();
    lookAtTarget = new THREE.Vector3();

    moveSensitivity = 1.0;

    // --- NEW: Relative offsets for the handle ---
    handlePositionOffset = new THREE.Vector3(0, 0, 0); // Default offset
    handleScaleMultiplier = new THREE.Vector3(1, 1, 1);   // Default multiplier

    /**
     * @param {object} options
     * @param {THREE.Scene} options.scene
     * @param {THREE.Group} options.gameGroup
     * @param {Array<THREE.Group>} options.controllers
     * @param {THREE.Camera} options.camera
     * @param {number} [options.moveSensitivity=1.0]
     * @param {THREE.Vector3} [options.moverStartPosition] - A *relative position offset* for the grab handle from the gameGroup's center.
     * @param {number|THREE.Vector3} [options.moverStartScale] - A *relative scale multiplier* for the grab handle.
     */
    constructor({ scene, gameGroup, controllers, camera, moveSensitivity, moverStartPosition, moverStartScale }) {
        if (!scene || !gameGroup || !controllers || !camera) {
            console.error("GameMover: Missing required constructor parameters.");
            return;
        }

        this.scene = scene;
        this.gameGroup = gameGroup;
        this.controllers = controllers;
        this.camera = camera;
        this.raycaster = new THREE.Raycaster();

        if (moveSensitivity !== undefined) {
            this.moveSensitivity = moveSensitivity;
        }

        // --- NEW: Store the relative offsets from constructor ---
        if (moverStartPosition instanceof THREE.Vector3) {
            this.handlePositionOffset.copy(moverStartPosition);
        }
        if (moverStartScale !== undefined) {
            if (typeof moverStartScale === 'number') {
                this.handleScaleMultiplier.set(moverStartScale, moverStartScale, moverStartScale);
            } else if (moverStartScale instanceof THREE.Vector3) {
                this.handleScaleMultiplier.copy(moverStartScale);
            }
        }

        this.createGrabHandle();
        this.bindControllerEvents();
        this.deactivate();
    }

    activate() {
        if (this.grabHandle) {
            this.grabHandle.visible = true;
        }
    }

    deactivate() {
        if (this.grabHandle) {
            this.grabHandle.visible = false;
        }
        this.isGrabbing = false;
        this.isHovering = false;
        this.activeController = null;
        this.updateVisuals(true);
    }

    createGrabHandle() {
        this.handleMaterial = new THREE.MeshBasicMaterial({
            color: this.colors.default,
            opacity: this.opacities.default,
            transparent: true,
        });
        const geometry = new THREE.BoxGeometry(1, 1, 1);
        this.grabHandle = new THREE.Mesh(geometry, this.handleMaterial);
        this.grabHandle.name = "GameMoverGrabHandle";
        this.scene.add(this.grabHandle);
        
        // Run once to set the initial position correctly
        this.updateHandlePosition();
    }

    bindControllerEvents() {
        this.controllers.forEach(controller => {
            controller.addEventListener('selectstart', this.onSelectStart);
            controller.addEventListener('selectend', this.onSelectEnd);
        });
    }

    setHoverState(isHovering) {
        if (!this.grabHandle || !this.grabHandle.visible) return;
        if (this.isHovering !== isHovering) {
            this.isHovering = isHovering;
            if (!this.isGrabbing) {
                this.updateVisuals();
            }
        }
    }

    onSelectStart = (event) => {
        if (!this.grabHandle || !this.grabHandle.visible || this.isGrabbing) return;
        const controller = event.target;
        this.raycaster.setFromXRController(controller);
        const intersects = this.raycaster.intersectObject(this.grabHandle);
        if (intersects.length > 0) {
            this.isGrabbing = true;
            this.activeController = controller;
            this.activeController.getWorldPosition(this.initialControllerPosition);
            this.initialGameGroupPosition.copy(this.gameGroup.position);
            if (controller.gamepad?.hapticActuators?.[0]) {
                controller.gamepad.hapticActuators[0].pulse(0.8, 50);
            }
            this.updateVisuals();
        }
    }

    onSelectEnd = () => {
        if (!this.isGrabbing) return;
        this.isGrabbing = false;
        this.activeController = null;
        this.updateVisuals();
    }

    updateVisuals(instant = false) {
        let targetColor, targetOpacity;
        if (this.isGrabbing) {
            targetColor = this.colors.grab;
            targetOpacity = this.opacities.grab;
        } else if (this.isHovering) {
            targetColor = this.colors.hover;
            targetOpacity = this.opacities.hover;
        } else {
            targetColor = this.colors.default;
            targetOpacity = this.opacities.default;
        }
        if (instant) {
            this.handleMaterial.color.set(targetColor);
            this.handleMaterial.opacity = targetOpacity;
        } else {
            gsap.to(this.handleMaterial.color, { r: (targetColor >> 16 & 255) / 255, g: (targetColor >> 8 & 255) / 255, b: (targetColor & 255) / 255, duration: 0.2 });
            gsap.to(this.handleMaterial, { opacity: targetOpacity, duration: 0.2 });
        }
    }

    update() {
        if (!this.grabHandle || !this.grabHandle.visible) return;

        if (this.isGrabbing && this.activeController) {
            const currentControllerPosition = new THREE.Vector3();
            this.activeController.getWorldPosition(currentControllerPosition);

            const delta = new THREE.Vector3().subVectors(currentControllerPosition, this.initialControllerPosition);
            delta.multiplyScalar(this.moveSensitivity);

            this.gameGroup.position.copy(this.initialGameGroupPosition).add(delta);

            this.lookAtTarget.set(this.camera.position.x, this.gameGroup.position.y, this.camera.position.z);
            this.gameGroup.lookAt(this.lookAtTarget);
        }


        // Always ensure the handle follows the game group
        this.updateHandlePosition();
    }

    /**
     * Synchronizes the grab handle's transform to match the gameGroup, applying
     * the relative position offset and scale multiplier.
     */
    updateHandlePosition() {
        if (!this.grabHandle) return;

        // --- 1. ROTATION: Match the game group's orientation first ---
        this.grabHandle.quaternion.copy(this.gameGroup.quaternion);

        // --- 2. SCALE: Start with game group scale, then apply the relative multiplier ---
        this.grabHandle.scale.copy(this.gameGroup.scale).multiply(this.handleScaleMultiplier);

        // --- 3. POSITION: Start at game group center, then add the rotated offset ---
        // Clone the offset so we don't modify the original
        const rotatedOffset = this.handlePositionOffset.clone();
        // The offset also needs to be scaled by the game group's scale
        rotatedOffset.multiply(this.gameGroup.scale);
        // Rotate the scaled offset to match the game group's orientation
        rotatedOffset.applyQuaternion(this.gameGroup.quaternion);
        // Set the handle's position to the game group's center plus the final rotated offset
        this.grabHandle.position.copy(this.gameGroup.position).add(rotatedOffset);
    }
}

export default GameMover;